/**
 * FeedTopBar — canonical Figma 38:904 "Без фильтров" top bar.
 *
 * ACT-INDEEP-PRODUCTION-FEED-FIGMA-REPAIR01 (Phase D + E)
 *
 * Figma binding authority:
 *   node 38:983  = title    "Без фильтров"
 *   node 38:985  = subtitle "Мысли, вопросы и разговоры сообщества."
 *   node 38:986  = 44×44 right-side compose affordance bound to /feed/compose
 *
 * Negative witnesses (no back chevron, no "Лента" title, no
 * filter/options placeholder) are enforced by FeedTopBar.test.tsx.
 *
 * Phase D shipped the structural composition (header + title + subtitle
 * + 44×44 compose affordance) with a transitional inline `style` on
 * the compose affordance to satisfy the F-4 geometry witness before
 * Phase-E CSS existed. Phase E (CSS) took over the visual layer
 * (color, padding, typography, hover/focus) and removed the inline
 * style — the affordance now reads its full presentation from
 * `./FeedTopBar.css`.
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import './FeedTopBar.css';

/**
 * Title text — Figma node 38:983.
 *
 * Pinned as a constant so a future rename cannot silently diverge
 * from Figma. The Playwright spec (F-4) and JSDOM authority suite
 * (FeedTopBar.test.tsx) both read this exact string.
 */
export const FEED_TOP_BAR_TITLE = 'Без фильтров';

/**
 * Subtitle text — Figma node 38:985.
 *
 * Pinned as a constant. Includes the trailing period verbatim.
 */
export const FEED_TOP_BAR_SUBTITLE = 'Мысли, вопросы и разговоры сообщества.';

/**
 * Accessible name for the compose affordance.
 *
 * Pinned in Russian to match the canonical Figma semantics.
 */
export const FEED_TOP_BAR_COMPOSE_ARIA_LABEL = 'Новый пост';

export interface FeedTopBarProps {
  // No props in Phase D — the top bar carries no dynamic state.
  // Reserved for future filters / segmented controls when Figma
  // authority for those surfaces arrives.
}

/**
 * Figma 38:904 "Без фильтров" Feed top bar.
 *
 * Renders:
 *   - title   "Без фильтров"
 *   - subtitle "Мысли, вопросы и разговоры сообщества."
 *   - 44×44 compose affordance → /feed/compose
 *
 * Explicitly does NOT render:
 *   - a back chevron (Figma has none)
 *   - a "Лента" title (this is not the Лента page)
 *   - a filter/options placeholder (Figma has none)
 */
export function FeedTopBar(_props: FeedTopBarProps = {}): JSX.Element {
  return (
    <header className="feed-top-bar" data-testid="feed-top-bar">
      <div className="feed-top-bar-text">
        <h1 className="feed-top-bar-title" data-testid="feed-top-bar-title">
          {FEED_TOP_BAR_TITLE}
        </h1>
        <p
          className="feed-top-bar-subtitle"
          data-testid="feed-top-bar-subtitle"
        >
          {FEED_TOP_BAR_SUBTITLE}
        </p>
      </div>
      <Link
        to={RoutePaths.FEED_COMPOSE}
        className="feed-top-bar-compose"
        data-testid="feed-top-bar-compose"
        aria-label={FEED_TOP_BAR_COMPOSE_ARIA_LABEL}
      >
        {/* Compose glyph (Figma 38:986). Aria-label provides the
            accessible name; the glyph is decorative. */}
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
      </Link>
    </header>
  );
}
